import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FolderKanban, ArrowLeft, ArrowRight, CheckCircle2, ListTodo, ShieldCheck, ChevronRight } from "lucide-react";
import { PersonalTaskDialog } from "@/pages/portal/PortalTaskEditor";
import { MilestonesPanel } from "@/components/projects/MilestonesPanel";

const PRIORITIES = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]];
const DONE = new Set(["completed", "cancelled"]);

export function NewTaskDialog({ open, onClose, onSaved, projectId = null }) {
  const [f, setF] = useState({ title: "", description: "", due_date: "", priority: "normal", project_id: projectId || "none" });
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && !projectId) api.get("/projects").then((r) => setProjects((r.data.items || []).filter((p) => !p.system_managed))).catch(() => {});
  }, [open, projectId]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/tasks", { title: f.title.trim(), description: f.description || null, due_date: f.due_date || null,
        priority: f.priority, project_id: f.project_id === "none" ? null : f.project_id });
      toast.success("Task created");
      setF({ title: "", description: "", due_date: "", priority: "normal", project_id: projectId || "none" });
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="portal-new-task-dialog">
        <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Task title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="portal-new-task-title" />
          <Textarea placeholder="Description (optional)" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="portal-new-task-desc" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="portal-new-task-due" />
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
              <SelectTrigger data-testid="portal-new-task-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{PRIORITIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!projectId && (
            <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
              <SelectTrigger data-testid="portal-new-task-project"><SelectValue placeholder="Project (optional)" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !f.title.trim()} data-testid="portal-new-task-save" className="bg-[#006090] hover:bg-[#004E75]">{busy ? "Saving…" : "Create Task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ t, onChanged, onOpen }) {
  const done = DONE.has(t.status);
  const toggle = async () => {
    try { await api.patch(`/tasks/${t.id}`, { status: done ? "not_started" : "completed" }); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <li className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`portal-personal-task-${t.id}`}>
      <Checkbox checked={done} onCheckedChange={toggle} className="mt-0.5" data-testid={`portal-personal-task-toggle-${t.id}`} />
      <button type="button" onClick={onOpen} className="min-w-0 text-left flex-1" data-testid={`portal-personal-task-open-${t.id}`}>
        <p className={`text-sm font-medium ${done ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</p>
        <p className="text-xs text-slate-500">{[t.project_name, t.due_date && `Due ${t.due_date.slice(0, 10)}`, t.importance !== "normal" && t.importance, t.checklist?.length && `${t.checklist.filter((c) => c.done).length}/${t.checklist.length} steps`].filter(Boolean).join(" · ")}</p>
      </button>
    </li>
  );
}

export function PersonalTasks({ reloadKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/tasks/mine").then((r) => setRows([...r.data.overdue, ...r.data.due_today, ...r.data.upcoming, ...r.data.completed.slice(0, 10)].filter((t) => !t.program_enrollment_id))).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);
  return (
    <section data-testid="portal-personal-tasks">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><ListTodo className="w-5 h-5 text-[#006090]" />Created by Me</h2>
          <p className="text-xs text-slate-500">Your own planning — separate from your official program requirements.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="portal-create-task"><Plus className="w-4 h-4 mr-1" />Add</Button>
      </div>
      {rows === null ? <p className="text-sm text-slate-400">Loading…</p> : rows.length === 0
        ? <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-300 bg-white p-4" data-testid="portal-personal-tasks-empty">Add your own to-dos here, like “Study for P&C exam” or “Research office locations”.</p>
        : <ul className="space-y-2">{rows.map((t) => <TaskRow key={t.id} t={t} onChanged={load} onOpen={() => setEdit(t.id)} />)}</ul>}
      <NewTaskDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      {edit && <PersonalTaskDialog taskId={edit} onClose={() => setEdit(null)} onChanged={load} />}
    </section>
  );
}

function ProjectDetail({ project, onBack }) {
  const [tasks, setTasks] = useState(null);
  const [p, setP] = useState(project);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const load = useCallback(() => {
    api.get(`/projects/${project.id}/tasks`).then((r) => setTasks(r.data.items || [])).catch(() => setTasks([]));
    api.get(`/projects/${project.id}`).then((r) => { setP(r.data.project); setMilestones(r.data.milestones || []); }).catch(() => {});
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (status) => {
    try { const r = await api.patch(`/projects/${p.id}`, { status }); setP(r.data); toast.success(status === "completed" ? "Project completed" : "Project reopened"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const done = p.status === "completed";
  return (
    <div className="space-y-4" data-testid="portal-project-detail">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="portal-project-back"><ArrowLeft className="w-4 h-4" />All projects</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="font-display text-xl font-bold text-slate-900">{p.name}{p.is_personal && <span className="ml-2 align-middle text-[10px] font-semibold rounded-full px-2 py-0.5 bg-violet-50 text-violet-700" data-testid="portal-project-personal">Personal</span>}</h2>{p.description && <p className="text-sm text-slate-600 mt-1">{p.description}</p>}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setStatus(done ? "active" : "completed")} data-testid="portal-project-complete"><CheckCircle2 className="w-4 h-4 mr-1" />{done ? "Reopen" : "Complete Project"}</Button>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="portal-project-add-task" className="bg-[#006090] hover:bg-[#004E75]"><Plus className="w-4 h-4 mr-1" />Add Task</Button>
        </div>
      </div>
      {tasks === null ? <p className="text-sm text-slate-400">Loading…</p> : tasks.length === 0 ? <p className="text-sm text-slate-500">No tasks yet.</p>
        : <ul className="space-y-2">{tasks.map((t) => <TaskRow key={t.id} t={t} onChanged={load} onOpen={() => setEdit(t.id)} />)}</ul>}
      {(p.is_personal || milestones.length > 0) && <div className="pt-2"><h3 className="text-sm font-semibold text-slate-800 mb-2">Milestones</h3>
        <MilestonesPanel projectId={p.id} milestones={milestones} canManage={!!p.can_manage_milestones} onChanged={load} /></div>}
      {edit && <PersonalTaskDialog taskId={edit} onClose={() => setEdit(null)} onChanged={load} />}
      <NewTaskDialog open={open} projectId={p.id} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

export function NewProjectDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", description: "", target_date: "" });
  const save = async () => {
    try {
      await api.post("/projects", { name: f.name.trim(), description: f.description || null, target_date: f.target_date || null, status: "active", project_scope: "personal" });
      toast.success("Project created"); setF({ name: "", description: "", target_date: "" }); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="portal-new-project-dialog">
        <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Project name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="portal-new-project-name" />
          <Textarea placeholder="Description (optional)" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="portal-new-project-desc" />
          <Input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} data-testid="portal-new-project-target" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!f.name.trim()} data-testid="portal-new-project-save" className="bg-[#006090] hover:bg-[#004E75]">Create Project</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CareerCard({ p, onOpen }) {
  const s = p?.stats || {};
  return (
    <button onClick={onOpen} data-testid="portal-career-conversion-card"
      className="w-full text-left rounded-2xl border border-[#006090]/25 bg-gradient-to-br from-[#006090]/[0.06] to-white p-5 hover:border-[#006090]/50 hover:shadow-sm transition-[border-color,box-shadow]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#006090] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Official program project{p?.program_label ? ` · ${p.program_label}` : ""}</p>
          <p className="font-display text-lg font-bold text-slate-900 mt-1">Career Conversion</p>
          <p className="text-sm text-slate-600 mt-0.5">Everything required on your path to agency ownership, managed with your district team.</p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 mt-1" />
      </div>
      {p && <div className="mt-4">
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#006090]" style={{ width: `${s.progress || 0}%` }} /></div>
        <p className="text-xs text-slate-500 mt-1.5" data-testid="portal-career-conversion-progress">{s.completed_tasks || 0} of {s.total_tasks || 0} active tasks complete · {s.progress || 0}%</p>
      </div>}
    </button>
  );
}

export default function PortalProjects({ goTo }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [programId, setProgramId] = useState(null);
  const load = useCallback(() => { api.get("/projects").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); api.get("/portal/program-journey").then((r) => setProgramId(r.data.project?.id || null)).catch(() => {}); }, [load]);
  if (sel) return <ProjectDetail project={sel} onBack={() => { setSel(null); load(); }} />;
  const career = (items || []).find((p) => p.system_managed);
  const program = (items || []).find((p) => p.id === programId);
  const mine = (items || []).filter((p) => !p.system_managed && p.id !== programId);
  return (
    <div className="space-y-6" data-testid="portal-projects">
      <CareerCard p={career} onOpen={() => goTo("journey", career && career.id === programId ? null : "training")} />
      {program && !program.system_managed && (
        <button onClick={() => goTo("journey")} data-testid={`portal-program-project-${program.id}`} className="w-full text-left rounded-2xl border border-[#006090]/30 bg-white p-5 hover:border-[#006090]">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#006090]">Your Program</p>
          <p className="font-display text-lg font-semibold text-slate-900 mt-0.5">{program.name}</p>
          <p className="text-sm text-slate-500 mt-1 inline-flex items-center gap-1">View your milestones and tasks in My Journey <ArrowRight className="w-3.5 h-3.5" /></p>
        </button>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Your Own Projects</h2>
          <p className="text-xs text-slate-500">Organise your own goals and to-dos — these don't change your program progress.</p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="portal-create-project" className="bg-[#006090] hover:bg-[#004E75]"><Plus className="w-4 h-4 mr-1" />New Project</Button>
      </div>
      {items === null ? <p className="text-sm text-slate-400">Loading…</p> : mine.length === 0
        ? <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-300 bg-white p-4" data-testid="portal-projects-empty">No personal projects yet — try “Licensing Study Plan” or “Find My Agency Office”.</p>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{mine.map((p) => (
          <button key={p.id} onClick={() => setSel(p)} data-testid={`portal-project-${p.id}`} className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300">
            <p className="font-semibold text-slate-900 flex items-center gap-2"><FolderKanban className="w-4 h-4 text-slate-400" />{p.name}</p>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-3"><div className="h-full bg-[#0D9488]" style={{ width: `${p.stats?.progress || 0}%` }} /></div>
            <p className="text-xs text-slate-500 mt-1.5 capitalize">{(p.status || "").replace("_", " ")} · {p.stats?.completed_tasks || 0}/{p.stats?.total_tasks || 0} tasks</p>
          </button>))}</div>}
      <NewProjectDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
